import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text } from '@react-three/drei';
import { ChartProps } from '../../types/visualization';
import * as THREE from 'three';

interface PieSlice3DProps {
  startAngle: number;
  endAngle: number;
  radius: number;
  height: number;
  color: string;
  label: string;
  value: number;
  percentage: number;
}

const PieSlice3D: React.FC<PieSlice3DProps> = ({ 
  startAngle, 
  endAngle, 
  radius, 
  height, 
  color, 
  label, 
  value, 
  percentage 
}) => {
  const groupRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = Math.sin(state.clock.elapsedTime * 0.3) * 0.05;
    }
  });

  const geometry = useMemo(() => {
    const shape = new THREE.Shape();
    const centerX = 0;
    const centerY = 0;
    
    // Start from center
    shape.moveTo(centerX, centerY);
    
    // Create arc
    const segments = 32;
    for (let i = 0; i <= segments; i++) {
      const angle = startAngle + (endAngle - startAngle) * (i / segments);
      const x = centerX + Math.cos(angle) * radius;
      const y = centerY + Math.sin(angle) * radius;
      shape.lineTo(x, y);
    }
    
    // Close the shape
    shape.lineTo(centerX, centerY);
    
    return new THREE.ExtrudeGeometry(shape, {
      depth: height,
      bevelEnabled: true,
      bevelThickness: 0.1,
      bevelSize: 0.1,
      bevelOffset: 0,
      bevelSegments: 1
    });
  }, [startAngle, endAngle, radius, height]);

  const midAngle = (startAngle + endAngle) / 2;
  const labelRadius = radius * 1.3;
  const labelX = Math.cos(midAngle) * labelRadius;
  const labelZ = Math.sin(midAngle) * labelRadius;

  return (
    <group ref={groupRef}>
      <mesh geometry={geometry}>
        <meshStandardMaterial color={color} />
      </mesh>
      
      {/* Value label */}
      <Text
        position={[labelX, height / 2 + 0.2, labelZ]}
        fontSize={0.3}
        color="black"
        anchorX="center"
        anchorY="middle"
      >
        {value.toLocaleString()}
      </Text>
      
      {/* Percentage label */}
      <Text
        position={[labelX, height / 2 - 0.2, labelZ]}
        fontSize={0.2}
        color="black"
        anchorX="center"
        anchorY="middle"
      >
        {percentage.toFixed(1)}%
      </Text>
      
      {/* Category label */}
      <Text
        position={[labelX, height / 2 + 0.6, labelZ]}
        fontSize={0.25}
        color="black"
        anchorX="center"
        anchorY="middle"
      >
        {label}
      </Text>
    </group>
  );
};

const PieChart3D: React.FC<ChartProps> = ({ 
  data, 
  config, 
  colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#84cc16', '#f97316'] 
}) => {
  if (!data || data.length === 0) {
    return <div>No data available</div>;
  }

  // Get configuration
  const labelColumn = config?.xAxis?.column || Object.keys(data[0])[0];
  const valueColumn = config?.yAxis?.primary?.columns?.[0] || Object.keys(data[0]).find(key => key !== labelColumn) || Object.keys(data[0])[1];

  // Format data for 3D pie chart
  const formattedData = data.map((item, index) => ({
    name: String(item[labelColumn]),
    value: Number(item[valueColumn]) || 0,
    color: colors[index % colors.length]
  }));

  // Calculate total and percentages
  const total = formattedData.reduce((sum, item) => sum + item.value, 0);
  const dataWithPercentages = formattedData.map(item => ({
    ...item,
    percentage: total > 0 ? (item.value / total) * 100 : 0
  }));

  // Calculate angles for each slice
  let currentAngle = 0;
  const slices = dataWithPercentages.map((item, index) => {
    const angle = (item.value / total) * Math.PI * 2;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle += angle;

    return {
      ...item,
      startAngle,
      endAngle,
      midAngle: (startAngle + endAngle) / 2
    };
  });

  return (
    <div style={{ width: '100%', height: '500px', background: 'transparent' }}>
      <Canvas camera={{ position: [0, 5, 8], fov: 50 }}>
        <ambientLight intensity={0.6} />
        <directionalLight position={[10, 10, 5]} intensity={1} />
        <pointLight position={[-10, -10, -5]} intensity={0.5} />
        
        {/* 3D Pie Slices */}
        {slices.map((slice, index) => (
          <PieSlice3D
            key={index}
            startAngle={slice.startAngle}
            endAngle={slice.endAngle}
            radius={2}
            height={1}
            color={slice.color}
            label={slice.name}
            value={slice.value}
            percentage={slice.percentage}
          />
        ))}

        {/* Title */}
        <Text
          position={[0, 4, 0]}
          fontSize={0.6}
          color="black"
          anchorX="center"
          anchorY="middle"
        >
          {config?.title || '3D Pie Chart'}
        </Text>

        <OrbitControls 
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          minPolarAngle={Math.PI / 6}
          maxPolarAngle={Math.PI - Math.PI / 6}
        />
      </Canvas>
    </div>
  );
};

export default PieChart3D;
